// Customer Favorites Service - Production Ready
// Handles customer favorites for sales representatives with Supabase integration

import { supabase } from './supabase';
import { logger } from '../utils/logger';

export const customerFavoritesService = {
  /**
   * Get all favorite customers for a sales rep
   * @param {string} salesRepId - The sales rep's UUID
   * @returns {Promise<Array>} Array of favorite customer objects with profile data
   */
  async getFavorites(salesRepId) {
    try {
      logger.debug('FavoritesService: Getting favorites for sales rep', { salesRepId });

      // First get the favorites
      const { data: favorites, error } = await supabase
        .from('customer_favorites')
        .select('*')
        .eq('sales_rep_id', salesRepId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error loading favorites from Supabase', { error: error.message, salesRepId });
        throw error;
      }

      if (!favorites || favorites.length === 0) {
        logger.debug('FavoritesService: No favorites found', { salesRepId });
        return [];
      }

      // Get all customer IDs
      const customerIds = favorites.map(fav => fav.customer_id);

      // Fetch customer details from user_profiles
      const { data: customers, error: customerError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, phone, company, specialty, location, profile_picture, npi_number')
        .in('id', customerIds);

      if (customerError) {
        logger.warn('Error loading customer profiles, using JSONB data', { error: customerError.message });
      }

      // Create a map of customer data by ID for easy lookup
      const customerMap = {};
      if (customers) {
        customers.forEach(customer => {
          customerMap[customer.id] = customer;
        });
      }

      // Transform the data to match expected format
      const transformedFavorites = favorites.map(fav => {
        // Use joined customer data first, fallback to customer_data JSONB
        const customer = customerMap[fav.customer_id];
        const customerData = fav.customer_data || {};

        // Construct full name from first_name and last_name
        const fullName = customer
          ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
          : customerData.name || 'Unknown Provider';

        // Format location
        let formattedLocation = 'Location Not Available';
        if (customer?.location) {
          const loc = customer.location;
          if (typeof loc === 'object') {
            formattedLocation = [loc.city, loc.state].filter(Boolean).join(', ') || 'Location Not Available';
          } else if (typeof loc === 'string') {
            formattedLocation = loc;
          }
        } else if (customerData.location) {
          formattedLocation = customerData.location;
        }

        return {
          favoriteId: fav.id,
          customerId: fav.customer_id,
          id: fav.customer_id, // Use customer_id as the main ID for matching
          notes: fav.notes,
          favoritedAt: fav.created_at,
          savedAt: fav.created_at, // For "Saved on" display
          // Customer profile data - prefer live data from user_profiles
          name: fullName || 'Unknown Provider',
          email: customer?.email || customerData.email,
          specialty: customer?.specialty || customerData.specialty || 'General Practice',
          company: customer?.company || customerData.company || customerData.hospital || 'Healthcare Provider',
          location: formattedLocation,
          address: formattedLocation, // Add address field for UI compatibility
          phone: customer?.phone || customerData.phone,
          preferredTime: 'lunch meetings', // Default for saved customers
          avatarUrl: customer?.profile_picture || customerData.avatarUrl,
          npi_number: customer?.npi_number || customerData.npi_number,
          source: 'Saved', // Mark as saved to show phone number
          isFavorite: true, // Always true for favorites list
          isRegistered: !!customer // True if found in user_profiles
        };
      });

      logger.debug('FavoritesService: Loaded favorites', { count: transformedFavorites.length });
      return transformedFavorites;
    } catch (error) {
      logger.error('FavoritesService: Error getting favorites', { error: error.message, salesRepId });
      return [];
    }
  },

  /**
   * Add a customer to favorites
   * @param {string} salesRepId - The sales rep's UUID
   * @param {string} customerId - The customer's UUID
   * @param {string} notes - Optional notes about the customer
   * @param {Object} customerData - Full customer object to store
   * @returns {Promise<Object|null>} Created favorite record or null
   */
  async addFavorite(salesRepId, customerId, notes = null, customerData = null) {
    try {
      logger.debug('FavoritesService: Adding favorite', { salesRepId, customerId, hasNotes: !!notes, hasCustomerData: !!customerData });

      const { data: newFavorite, error } = await supabase
        .from('customer_favorites')
        .insert([{
          sales_rep_id: salesRepId,
          customer_id: customerId,
          notes: notes,
          customer_data: customerData || {}
        }])
        .select()
        .single();

      if (error) {
        // Handle unique constraint violation (already favorited)
        if (error.code === '23505') {
          logger.warn('Customer already favorited', { salesRepId, customerId });
          return { alreadyExists: true };
        }
        logger.error('Error adding favorite', { error: error.message, salesRepId, customerId });
        throw error;
      }

      logger.debug('FavoritesService: Successfully added favorite', { favoriteId: newFavorite.id });
      return {
        favoriteId: newFavorite.id,
        customerId: newFavorite.customer_id,
        customerName: customerData?.name || 'Unknown',
        notes: newFavorite.notes,
        favoritedAt: newFavorite.created_at
      };
    } catch (error) {
      logger.error('FavoritesService: Error adding favorite', { error: error.message, salesRepId, customerId });
      return null;
    }
  },

  /**
   * Remove a customer from favorites
   * @param {string} salesRepId - The sales rep's UUID
   * @param {string} customerId - The customer's UUID
   * @returns {Promise<boolean>} Success status
   */
  async removeFavorite(salesRepId, customerId) {
    try {
      logger.debug('FavoritesService: Removing favorite', { salesRepId, customerId });

      const { error } = await supabase
        .from('customer_favorites')
        .delete()
        .eq('sales_rep_id', salesRepId)
        .eq('customer_id', customerId);

      if (error) {
        logger.error('Error removing favorite', { error: error.message, salesRepId, customerId });
        throw error;
      }

      logger.debug('FavoritesService: Successfully removed favorite', { salesRepId, customerId });
      return true;
    } catch (error) {
      logger.error('FavoritesService: Error removing favorite', { error: error.message, salesRepId, customerId });
      return false;
    }
  },

  /**
   * Check if a customer is favorited by a sales rep
   * @param {string} salesRepId - The sales rep's UUID
   * @param {string} customerId - The customer's UUID
   * @returns {Promise<boolean>} True if favorited, false otherwise
   */
  async isFavorited(salesRepId, customerId) {
    try {
      const { data, error } = await supabase
        .from('customer_favorites')
        .select('id')
        .eq('sales_rep_id', salesRepId)
        .eq('customer_id', customerId)
        .maybeSingle();

      if (error) {
        logger.error('Error checking favorite status', { error: error.message, salesRepId, customerId });
        return false;
      }

      return !!data;
    } catch (error) {
      logger.error('FavoritesService: Error checking favorite status', { error: error.message, salesRepId, customerId });
      return false;
    }
  },

  /**
   * Get favorite status for multiple customers (for bulk checking)
   * @param {string} salesRepId - The sales rep's UUID
   * @param {Array<string>} customerIds - Array of customer UUIDs
   * @returns {Promise<Object>} Object mapping customerId -> boolean
   */
  async getFavoriteStatuses(salesRepId, customerIds) {
    try {
      if (!customerIds || customerIds.length === 0) {
        return {};
      }

      const { data: favorites, error } = await supabase
        .from('customer_favorites')
        .select('customer_id')
        .eq('sales_rep_id', salesRepId)
        .in('customer_id', customerIds);

      if (error) {
        logger.error('Error getting favorite statuses', { error: error.message, salesRepId, customerCount: customerIds.length });
        return {};
      }

      // Create a map of customer_id -> true for favorited customers
      const statusMap = {};
      customerIds.forEach(id => {
        statusMap[id] = false;
      });

      favorites.forEach(fav => {
        statusMap[fav.customer_id] = true;
      });

      return statusMap;
    } catch (error) {
      logger.error('FavoritesService: Error getting favorite statuses', { error: error.message, salesRepId });
      return {};
    }
  },

  /**
   * Update notes for a favorite customer
   * @param {string} salesRepId - The sales rep's UUID
   * @param {string} customerId - The customer's UUID
   * @param {string} notes - Updated notes
   * @returns {Promise<boolean>} Success status
   */
  async updateFavoriteNotes(salesRepId, customerId, notes) {
    try {
      logger.debug('FavoritesService: Updating favorite notes', { salesRepId, customerId, notesLength: notes?.length });

      const { error } = await supabase
        .from('customer_favorites')
        .update({ notes: notes })
        .eq('sales_rep_id', salesRepId)
        .eq('customer_id', customerId);

      if (error) {
        logger.error('Error updating favorite notes', { error: error.message, salesRepId, customerId });
        throw error;
      }

      logger.debug('FavoritesService: Successfully updated favorite notes', { salesRepId, customerId });
      return true;
    } catch (error) {
      logger.error('FavoritesService: Error updating favorite notes', { error: error.message, salesRepId, customerId });
      return false;
    }
  },

  /**
   * Get analytics about favorites (for insights/reporting)
   * @param {string} salesRepId - The sales rep's UUID
   * @returns {Promise<Object>} Analytics data
   */
  async getFavoritesAnalytics(salesRepId) {
    try {
      const favorites = await this.getFavorites(salesRepId);

      const analytics = {
        totalFavorites: favorites.length,
        topSpecialties: {},
        favoritesByMonth: {},
        averageNotesLength: 0,
        notesCount: 0
      };

      favorites.forEach(fav => {
        // Count specialties
        const specialty = fav.specialty || 'Unknown';
        analytics.topSpecialties[specialty] = (analytics.topSpecialties[specialty] || 0) + 1;

        // Count by month favorited
        const month = new Date(fav.favoritedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
        analytics.favoritesByMonth[month] = (analytics.favoritesByMonth[month] || 0) + 1;

        // Notes statistics
        if (fav.notes && fav.notes.trim()) {
          analytics.notesCount++;
          analytics.averageNotesLength += fav.notes.length;
        }
      });

      if (analytics.notesCount > 0) {
        analytics.averageNotesLength = Math.round(analytics.averageNotesLength / analytics.notesCount);
      }

      return analytics;
    } catch (error) {
      logger.error('FavoritesService: Error getting analytics', { error: error.message, salesRepId });
      return {
        totalFavorites: 0,
        topSpecialties: {},
        favoritesByMonth: {},
        averageNotesLength: 0,
        notesCount: 0
      };
    }
  }
};

export default customerFavoritesService;